import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import IdentifyPage from 'researcher/application/components/IdentifyPage';
import GlobalLoading from 'researcher/components/GlobalLoading';
import { addMessage, getWithoutPopup } from 'researcher/application/actions';
var SUPPORTED_LANGUAGES = ['br', 'cn', 'en', 'es', 'jp'];
var DEFAULT_LANGUAGE = 'en';
var GENERIC_ERROR = 'Invalid preferred code error';

/**
 * Resolves a public preferred group joining link (`/:lang/c/:preferredGroupCode`) so the link can be
 * shared on the Rubriq domain instead of the megatron domain.
 *
 * Megatron still owns the lookup, the eligibility checks and the group join. It answers this route
 * with JSON when the request comes from an allowed Rubriq origin and asks for JSON:
 *
 *   { groupCode, isRubriqGroup, redirectUrl }  // redirectUrl is always absolute
 *   { error }                                  // unusable code
 *
 * A successful resolution is followed with a full page load, even when `redirectUrl` is on this
 * origin, because the group join happened in megatron's session and any state this app loaded at
 * boot (user, preferred group, purchase criteria) is stale by the time we get here.
 */
var MarketingCodeRedirect = function MarketingCodeRedirect(_ref) {
  var notifyError = _ref.notifyError,
    resolveCode = _ref.resolveCode;
  var _useParams = useParams(),
    lang = _useParams.lang,
    preferredGroupCode = _useParams.preferredGroupCode;
  var location = useLocation();
  var navigate = useNavigate();
  // React StrictMode invokes effects twice in development. Resolving twice would redirect twice.
  var resolving = useRef(false);
  useEffect(function () {
    if (resolving.current) {
      return;
    }
    resolving.current = true;
    var language = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;

    // Megatron's CORS headers are not applied to error responses, so the browser may hide the
    // error payload from us and surface a network failure instead. Every shape of failure lands
    // the researcher on the Rubriq landing page with an explanation.
    var fail = function fail(error) {
      notifyError(error || GENERIC_ERROR);
      navigate("/".concat(language, "/rubriq"), {
        replace: true
      });
    };
    resolveCode(language, preferredGroupCode, location.search).then(function (json) {
      var _ref2 = json || {},
        error = _ref2.error,
        redirectUrl = _ref2.redirectUrl;
      if (error || !redirectUrl) {
        fail(error);
        return;
      }
      window.location.replace(redirectUrl);
    }).catch(function () {
      return fail();
    });
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IdentifyPage, {
    robots: "noindex, nosnippet, noarchive",
    title: "Rubriq"
  }), /*#__PURE__*/React.createElement(GlobalLoading, {
    className: "position-relative"
  }));
};
var mapDispatchToProps = function mapDispatchToProps(dispatch) {
  return {
    notifyError: function notifyError(message) {
      return dispatch(addMessage({
        type: 'error',
        message: message
      }));
    },
    resolveCode: function resolveCode(language, code, search) {
      return dispatch(getWithoutPopup("/".concat(language, "/c/").concat(encodeURIComponent(code)).concat(search)));
    }
  };
};
export default connect(null, mapDispatchToProps)(MarketingCodeRedirect);